import { Button, Card, Checkbox, Label } from 'flowbite-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { useBuildingContext } from '../../components/BuildingContext.tsx';
import RequiresBuilding from '../../components/RequiresBuilding.tsx';
import { toastError } from '../../components/Toast.tsx';
import { useMigrateSyntheticRoomsMutation, useRegenerateDefaultsMutation } from '../../lib/model/kantoordagApi.ts';
import { Migrators } from '../../lib/model/migration/migrationTypes.ts';
import { MigrationProgress, useMigrateLazyQuery } from '../../lib/model/migration/migrationStreamApi.ts';
import MigrationProgressPanel from './MigrationProgressPanel.tsx';

export default function Migration() {
    return (
        <RequiresBuilding>
            <MigrationInternal/>
        </RequiresBuilding>
    )
}

/**
 * Fires a toast exactly once when a migration run (as tracked by migrationStreamApi)
 * transitions into "done" or "error", instead of on every render while it's still
 * "running". Shared by both migrator sections below.
 */
function useMigrationResultToast(progress: MigrationProgress | undefined, successMessage: string, failureMessage: string) {
    const reportedFor = useRef<MigrationProgress | undefined>(undefined);

    useEffect(() => {
        if (progress === undefined || progress === reportedFor.current) {
            return;
        }
        if (progress.status === "done") {
            reportedFor.current = progress;
            toast.success(successMessage);
        } else if (progress.status === "error") {
            reportedFor.current = progress;
            toast.error(`${failureMessage}: ${progress.errorMessage ?? ""}`);
        }
    }, [progress, successMessage, failureMessage]);
}

function MigrationInternal() {
    const { t } = useTranslation("migration");
    const selectedBuilding = useBuildingContext();

    const [regenerateDefaults, regenerateDefaultsResult] = useRegenerateDefaultsMutation()

    const [migrateSyntheticRooms, migrateSyntheticRoomsResult] = useMigrateSyntheticRoomsMutation()

    const [triggerKantoordagMigration, kantoordagMigrationResult] = useMigrateLazyQuery()
    useMigrationResultToast(kantoordagMigrationResult.data, t("kantoordag_views_migrated"), t("migrate_kantoordag_views_failed"))

    const [triggerMyReservationsMigration, myReservationsMigrationResult] = useMigrateLazyQuery()
    const [includeHistory, setIncludeHistory] = useState<boolean>(false)
    useMigrationResultToast(myReservationsMigrationResult.data, t("my_reservations_regenerated"), t("regenerate_my_reservations_failed"))

    function regenerateAllDefaults() {
        regenerateDefaults().unwrap()
            .then(() => toast.success(t("defaults_regenerated")))
            .catch(() => toastError({ api: regenerateDefaultsResult, message: t("regenerate_defaults_failed") }))
    }

    function migrateAllSyntheticRooms() {
        migrateSyntheticRooms().unwrap()
            .then(() => toast.success(t("synthetic_rooms_migrated")))
            .catch(() => toastError({ api: migrateSyntheticRoomsResult, message: t("migrate_synthetic_rooms_failed") }))
    }

    function migrateKantoordagViews() {
        triggerKantoordagMigration({
            migrator: Migrators.KantoordagViewGenerator,
            params: { buildingName: selectedBuilding.name, fromDate: null },
            runId: Date.now(),
        })
    }

    function migrateMyReservationsViews() {
        triggerMyReservationsMigration({
            migrator: Migrators.MyReservationViewGenerator,
            params: { includeHistory, fromDate: null },
            runId: Date.now(),
        })
    }

    const kantoordagMigrationRunning = kantoordagMigrationResult.data?.status === "running"
    const myReservationsMigrationRunning = myReservationsMigrationResult.data?.status === "running"

    return (
        <Card>
            <div>
                <Button onClick={migrateKantoordagViews} disabled={kantoordagMigrationRunning}>
                    {t("migrate_kantoordag_views", { building: selectedBuilding.name })}
                </Button>
                <MigrationProgressPanel progress={kantoordagMigrationResult.data}/>
            </div>

            <div>
                <Button onClick={regenerateAllDefaults} disabled={regenerateDefaultsResult.isLoading}>
                    {t("regenerate_defaults")}
                </Button>
            </div>

            <div>
                <Button onClick={migrateAllSyntheticRooms} disabled={migrateSyntheticRoomsResult.isLoading}>
                    {t("migrate_synthetic_rooms")}
                </Button>
            </div>

            <div>
                <div className="flex items-center gap-2">
                    <Checkbox
                        id="includeHistory"
                        checked={includeHistory}
                        onChange={(e) => setIncludeHistory(e.target.checked)}
                    />
                    <Label htmlFor="includeHistory">{t("regenerate_my_reservations_include_history")}</Label>
                </div>
                <Button onClick={migrateMyReservationsViews} disabled={myReservationsMigrationRunning}>
                    {t("regenerate_my_reservations")}
                </Button>
                <MigrationProgressPanel progress={myReservationsMigrationResult.data}/>
            </div>
        </Card>
    )
}
